import { Link } from "react-router-dom";

// PropertySearchToolbar — canonical .search-toolbar substrate.
//
// Three regions per design/search.html:
//   .search-toolbar__count   — DRF pagination `count` bound to the
//                              <strong> answer-shape.
//   .search-toolbar__sort    — DRF OrderingFilter mapping.
//   .search-toolbar__view    — list ↔ map view toggle.
//
// Sort options (per R2 STEP R2.7; updated added RIC-4.2 / Issue #167):
//   * newest     → ordering=-created_at  (default; ordering = -created_at
//                  in the viewset)
//   * oldest     → ordering=created_at
//   * updated    → ordering=-updated_at  (RIC-4.2 — viewset
//                  ordering_fields already allows updated_at)
//   * popular    → ordering=-likes_count_total,-created_at  (RIC-4.4 —
//                  likes_count_total whitelisted in ordering_fields)
//   * price_asc  → ordering=price
//   * price_desc → ordering=-price
//
// "Trending" / "Recommended" / "least popular" are NOT rendered.
// Per R2 STEP R2.7: render only what the backend supports.
//
// View toggle: paired-surface invariant (design-map.md §5) — /search
// and /map are the same query in two render modes. The toolbar is the
// seam between them and renders both modes side by side; the
// `activeView` prop tells it which side is current.
//
// Ownership:
//   * `count` and `sort` are owned by the parent.
//   * Submitting the sort fires onSortChange(value) immediately —
//     no separate "Apply" affordance for sort (matches design).
//
// `count` may be a number (DRF paginated) or null (still loading).
//
// R6.3 props (additive; defaults preserve existing /search behavior):
//   * `countNode`: optional React node that replaces the default
//      `<strong>{count}</strong> об'єктів` content. /map uses this to
//      surface the marker-ceiling truncation explicitly when
//      count > 100. When omitted, the default count rendering is
//      used.
//   * `hideSort`: when true, the .search-toolbar__sort region is not
//      rendered. /map is its own ordering (design-map.md §3 map —
//      toolbar carries no sort on the map surface).
//   * `activeView`: "list" (default — /search) or "map". Controls
//      which view-toggle button gets `.is-active` + `aria-pressed`.
//      The inactive side renders as a <Link> to its surface.

function PropertySearchToolbar({
  count,
  countNode = null,
  sort,
  onSortChange,
  hideSort = false,
  activeView = "list",
  listHref = "/search",
  mapHref = "/map",
  hideViewToggle = false,
}) {
  const isListActive = activeView !== "map";

  return (
    <header
      className="search-toolbar"
      aria-label="Сортування та вигляд результатів"
    >
      <div className="search-toolbar__count">
        {countNode !== null && countNode !== undefined ? (
          countNode
        ) : count === null || count === undefined ? (
          <>
            <strong>—</strong> об'єктів
          </>
        ) : (
          <>
            <strong>{count}</strong> об'єктів
          </>
        )}
      </div>

      {hideSort ? null : (
        <label className="search-toolbar__sort">
          <span>Сортувати:</span>
          <select
            className="form-select"
            aria-label="Сортування"
            value={sort || "newest"}
            onChange={(event) => {
              if (onSortChange) onSortChange(event.target.value);
            }}
          >
            <option value="newest">Найновіші</option>
            <option value="oldest">Найстаріші</option>
            <option value="updated">Нещодавно оновлені</option>
            <option value="popular">Найпопулярніші</option>
            <option value="price_asc">За ціною ↑</option>
            <option value="price_desc">За ціною ↓</option>
          </select>
        </label>
      )}

      {hideViewToggle ? null : (
        <div
          className="search-toolbar__view"
          role="group"
          aria-label="Вигляд результатів"
        >
          {isListActive ? (
            <button
              type="button"
              className="view-toggle is-active"
              data-view="list"
              aria-pressed="true"
            >
              <i className="fa-solid fa-list" aria-hidden="true" />
              <span>Список</span>
            </button>
          ) : (
            <Link
              to={listHref}
              className="view-toggle"
              data-view="list"
              aria-label="Список об'єктів"
            >
              <i className="fa-solid fa-list" aria-hidden="true" />
              <span>Список</span>
            </Link>
          )}

          {isListActive ? (
            <Link
              to={mapHref}
              className="view-toggle"
              data-view="map"
              aria-label="Карта об'єктів"
            >
              <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
              <span>Карта</span>
            </Link>
          ) : (
            <button
              type="button"
              className="view-toggle is-active"
              data-view="map"
              aria-pressed="true"
            >
              <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
              <span>Карта</span>
            </button>
          )}
        </div>
      )}
    </header>
  );
}

export default PropertySearchToolbar;
